import React, { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import Page from 'src/components/Page';
import { getUser } from 'src/utils/Common';
import { makeStyles } from '@mui/styles';
import {
  Alert,
  Backdrop,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Dialog,
  DialogActions,
  CircularProgress,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Snackbar,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  Autocomplete
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { getTourInformations } from 'src/store/slices/commonSlice';
import CloseIcon from '@mui/icons-material/Close';
import axios from 'src/utils/axios';
import { confirmAlert } from 'react-confirm-alert';
import MoodBadIcon from '@mui/icons-material/MoodBad';
import TagFacesIcon from '@mui/icons-material/TagFaces';
import RouteIcon from '@mui/icons-material/Route';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import CheckIcon from '@mui/icons-material/Check';
import RemoveIcon from '@mui/icons-material/Remove';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { getRegions } from 'src/store/slices/assigmentSlice';

const ORANGE = '#FF9800';
const DARK = '#424242';

const darkButtonSx = {
  color: ORANGE,
  backgroundColor: DARK,
  borderRadius: '10px',
  fontWeight: 600,
  fontSize: '13px',
  height: '36px',
  '&:hover': { backgroundColor: '#212121' }
};

const lightButtonSx = {
  color: DARK,
  borderRadius: '10px',
  fontWeight: 600,
  fontSize: '13px'
};

const inputSx = {
  '& .MuiOutlinedInput-root': { borderRadius: '10px', backgroundColor: '#fff', fontSize: '13px' },
  '& .MuiInputLabel-root': { fontSize: '13px' }
};

const listboxProps = { sx: { fontSize: '13px' } };

const DAY_FIELDS = [
  { field: 'PAZARTESI', headerName: 'Pazartesi', short: 'Pzt' },
  { field: 'SALI', headerName: 'Salı', short: 'Sal' },
  { field: 'CARSAMBA', headerName: 'Çarşamba', short: 'Çar' },
  { field: 'PERSEMBE', headerName: 'Perşembe', short: 'Per' },
  { field: 'CUMA', headerName: 'Cuma', short: 'Cum' },
  { field: 'CUMARTESI', headerName: 'Cumartesi', short: 'Cmt' },
  { field: 'PAZAR', headerName: 'Pazar', short: 'Paz' }
];

const DAY_OPTIONS = [
  { value: 1, label: 'Ertesi Gün' },
  { value: 2, label: 'Aynı Gün' }
];

const useStyles = makeStyles((theme) => ({
  root: {
    fontFamily: 'Segoe UI',
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    padding: '16px 24px',
    boxSizing: 'border-box',
    [theme.breakpoints.down('sm')]: {
      padding: '12px'
    }
  },
  backdrop: {
    zIndex: 1500,
    color: '#fff'
  },
  card: {
    border: '1px solid #e0e0e0',
    borderRadius: '16px',
    backgroundColor: '#fff',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    overflow: 'hidden'
  },
  cardHeader: {
    backgroundColor: '#f5f5f5',
    color: DARK,
    padding: '10px 16px',
    borderBottom: '1px solid #e0e0e0',
    '& .MuiCardHeader-title': {
      fontSize: '12px',
      fontWeight: '700',
      letterSpacing: '0.5px',
      textTransform: 'uppercase'
    },
    '& .MuiCardHeader-subheader': {
      fontSize: '11px',
      color: '#757575'
    }
  },
  sectionLabel: {
    fontSize: '10px',
    fontWeight: '700',
    color: '#78909c',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '6px'
  },
  infoBox: {
    padding: '8px 12px',
    backgroundColor: '#fafafa',
    borderRadius: '10px',
    border: '1px solid #e0e0e0',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
    position: 'relative',
    overflow: 'hidden',
    textAlign: 'center',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: '3px',
      backgroundColor: ORANGE
    }
  },
  infoLabel: {
    fontWeight: '600',
    fontSize: '10px',
    color: '#546e7a',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  },
  infoValue: {
    fontSize: '14px',
    fontWeight: '700',
    color: '#263238'
  },
  grid: {
    border: 'none',
    '& .MuiDataGrid-columnHeaders': {
      backgroundColor: '#f5f5f5',
      borderBottom: '1px solid #e0e0e0'
    },
    '& .MuiDataGrid-columnHeaderTitle': {
      fontWeight: '700',
      fontSize: '11px',
      color: DARK
    },
    '& .MuiDataGrid-row:hover': {
      backgroundColor: '#fff8e1'
    },
    '& .MuiDataGrid-cell': {
      fontSize: '12px',
      color: '#263238'
    },
    '& .MuiDataGrid-footerContainer, & .MuiTablePagination-root, & .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows, & .MuiTablePagination-select': {
      fontSize: '12px'
    }
  },
  dialogSection: {
    marginTop: '16px',
    padding: '12px 14px',
    borderRadius: '12px',
    border: '1px solid #e0e0e0',
    backgroundColor: '#fafafa'
  },
  dayTile: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '2px',
    height: '56px',
    borderRadius: '10px',
    border: '1px solid #e0e0e0',
    backgroundColor: '#fff',
    cursor: 'pointer',
    userSelect: 'none',
    transition: 'all 0.15s',
    '&:hover': { borderColor: ORANGE }
  },
  dayTileActive: {
    backgroundColor: DARK,
    borderColor: DARK,
    '&:hover': { backgroundColor: '#212121', borderColor: '#212121' }
  },
  settingRow: {
    display: 'grid',
    gridTemplateColumns: '140px 1fr',
    alignItems: 'center',
    gap: '12px',
    padding: '8px 0'
  },
  toggleGroup: {
    width: '100%',
    backgroundColor: '#fff',
    '& .MuiToggleButton-root': {
      flex: 1,
      fontSize: '12px',
      fontWeight: 600,
      textTransform: 'none',
      padding: '6px 8px',
      color: '#546e7a',
      borderColor: '#e0e0e0',
      '&:first-of-type': { borderRadius: '10px 0 0 10px' },
      '&:last-of-type': { borderRadius: '0 10px 10px 0' }
    },
    '& .MuiToggleButton-root.Mui-selected': {
      backgroundColor: DARK,
      color: ORANGE,
      '&:hover': { backgroundColor: '#212121' }
    }
  }
}));

// Gün aktif/pasif göstergesi (salt okunur)
function DayMark({ active }) {
  return active ? (
    <Box sx={{ width: 22, height: 22, borderRadius: '50%', backgroundColor: ORANGE, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <CheckIcon sx={{ fontSize: 14, color: '#fff' }} />
    </Box>
  ) : (
    <Box sx={{ width: 22, height: 22, borderRadius: '50%', backgroundColor: '#eceff1', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <RemoveIcon sx={{ fontSize: 14, color: '#b0bec5' }} />
    </Box>
  );
}

function TourManagement() {
  const SessionUser = getUser();
  const dispatch = useDispatch();
  const classes = useStyles();

  const [tourGeneralInformations, setTourGeneralInformations] = React.useState(
    []
  );
  const [tourInfoUpdatePopUp, setTourInfoUpdatePopUp] = React.useState(false);
  const [tourUpdateLoading, setTourUpdateLoading] = React.useState(false);
  const [regionList, setRegionList] = React.useState([]);
  const [selectedUpdateRegion, setSelectedUpdateRegion] = React.useState(null);

  const tourNoInputRef = useRef(null);
  const tourNameInputRef = useRef(null);

  const [tourMondayCheck, setTourMondayCheck] = React.useState([]);
  const [tourTuesdayCheck, setTourTuesdayCheck] = React.useState([]);
  const [tourWednesdayCheck, setTourWednesdayCheck] = React.useState([]);
  const [tourThursdayCheck, setTourThursdayCheck] = React.useState([]);
  const [tourFridayCheck, setTourFridayCheck] = React.useState([]);
  const [tourSaturdayCheck, setTourSaturdayCheck] = React.useState([]);
  const [tourSundayCheck, setTourSundayCheck] = React.useState([]);

  const [tourSaturdayWorkingCheck, setTourSaturdayWorkingCheck] =
    React.useState([]);
  const [tourShipmentCheck, setTourShipmentCheck] = React.useState([]);
  const [tourDeliveryCheck, setTourDeliveryCheck] = React.useState([]);

  const [tourNoCheck, setTourNoCheck] = React.useState([]);
  const [tourNameCheck, setTourNameCheck] = React.useState([]);

  const [loading, setLoading] = React.useState(false);
  const [uyari, setUyari] = React.useState(false);
  const [responseMessage, setresponseMessage] = React.useState({
    ErrorCode: '0',
    ErrorDescription: 'success_message'
  });

  useEffect(() => {
    getRegionList();
    getTourInfo(SessionUser.TopServiceShopCode);
  }, []);

  const getRegionList = () => {
    setLoading(true);
    dispatch(getRegions())
      .then((response) => {
        const data = response.payload || [];
        const filteredData = data.slice(1);
        setRegionList(filteredData);

        const defaultRegion = filteredData.find(
          (i) => i.Id === SessionUser?.TopServiceShopCode
        );
        setSelectedUpdateRegion(defaultRegion);
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
      });
  };

  const getTourInfo = (SelectedService) => {
    setLoading(true);
    dispatch(getTourInformations(SelectedService))
      .then((response) => {
        const data = response.payload || [];
        setTourGeneralInformations(data);
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        return;
      });
  };

  const handleGetSelectedServiceTourInfo = () => {
    getTourInfo(
      selectedUpdateRegion.Id
        ? selectedUpdateRegion.Id
        : SessionUser.TopServiceShopCode
    );
  };

  const createTour = () => {
    if (
      tourNoInputRef.current.value.length <= 0 ||
      tourNameInputRef.current.value.length <= 0 ||
      !selectedUpdateRegion?.Id
    ) {
      setresponseMessage({
        ErrorCode: 1000,
        ErrorDescription:
          'Lütfen Tur Numarası, Tur adı veya Bölge alanlarını doldurunuz!...'
      });
      setUyari(true);
      return;
    }

    const data = {
      TourNo: tourNoInputRef.current.value,
      TourName: tourNameInputRef.current.value,
      TopServiceShopCode: selectedUpdateRegion?.Id
    };
    setLoading(true);
    axios
      .post('TourManagementCreateTours', data, {
        headers: { 'Content-Type': 'application/json' }
      })
      .then((response) => {
        if (response.data.ErrorCode === '0') {
          setresponseMessage({
            ErrorCode: 0,
            ErrorDescription: 'İşleminiz başarılıyla gerçekleşmiştir...'
          });
          setUyari(true);

          getTourInfo(selectedUpdateRegion?.Id);

          tourNoInputRef.current.value = '';
          tourNameInputRef.current.value = '';

          setLoading(false);
          return;
        } else {
          setresponseMessage({
            ErrorCode: 1000,
            ErrorDescription: response.data.ErrorDescription
          });
          setUyari(true);

          tourNoInputRef.current.value = '';
          tourNameInputRef.current.value = '';

          setLoading(false);
          return;
        }
      })
      .catch((err) => {
        setLoading(false);
        return;
      });
  };

  const deleteTour = (row) => {
    const data = {
      TourNo: row.Tur,
      TourName: row.TUR_ADI,
      TopServiceShopCode: selectedUpdateRegion?.Id
    };

    const onConfirm = () => {
      setLoading(true);
      axios
        .post('TourManagementDeleteTours', data, {
          headers: { 'Content-Type': 'application/json' }
        })
        .then((response) => {
          if (response.data.ErrorCode === '0') {
            setresponseMessage({
              ErrorCode: 0,
              ErrorDescription: 'İşleminiz başarılıyla gerçekleşmiştir...'
            });
            setUyari(true);
            getTourInfo(selectedUpdateRegion?.Id);
            setLoading(false);
            return;
          } else {
            setLoading(false);
            return;
          }
        })
        .catch((err) => {
          setLoading(false);
          return;
        });
    };

    confirmAlert({
      customUI: ({ onClose }) => (
        <Card className={classes.card} sx={{ width: 380, maxWidth: '90vw' }}>
          <CardHeader
            className={classes.cardHeader}
            avatar={<WarningAmberIcon sx={{ color: '#c62828' }} />}
            title="Tur Silinecek"
          />
          <CardContent>
            <Typography sx={{ fontSize: 13, color: '#263238' }}>
              <b>{row.Tur}</b> - {row.TUR_ADI} turunu silmek istediğinizden emin misiniz?
            </Typography>
            <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
              <Button sx={lightButtonSx} onClick={onClose}>Hayır</Button>
              <Button
                sx={{ ...darkButtonSx, color: '#fff', backgroundColor: '#c62828', '&:hover': { backgroundColor: '#b71c1c' } }}
                startIcon={<DeleteIcon />}
                onClick={() => { onConfirm(); onClose(); }}
              >
                Evet, Sil
              </Button>
            </Stack>
          </CardContent>
        </Card>
      )
    });
  };

  // Kaydetme sürerken popup kapatılamaz (işlem sunucuda tamamlanabilir)
  const closeTourInfoPopUp = () => {
    if (tourUpdateLoading) return;
    setTourInfoUpdatePopUp(false);
  };

  const updateTourInformationClick = (row) => {
    setTourMondayCheck(row.PAZARTESI);
    setTourTuesdayCheck(row.SALI);
    setTourWednesdayCheck(row.CARSAMBA);
    setTourThursdayCheck(row.PERSEMBE);
    setTourFridayCheck(row.CUMA);
    setTourSaturdayCheck(row.CUMARTESI);
    setTourSundayCheck(row.PAZAR);

    setTourSaturdayWorkingCheck(row.CUMARTESI_CALISMA_DURUMU);
    setTourShipmentCheck(row.YUKLEMEID);
    setTourDeliveryCheck(row.TESLIMATID);

    setTourNoCheck(row.Tur);
    setTourNameCheck(row.TUR_ADI);

    setTourInfoUpdatePopUp(true);
  };

  const updateInformationsOfTour = () => {
    const data = {
      Monday: tourMondayCheck,
      Tuesday: tourTuesdayCheck,
      Wednesday: tourWednesdayCheck,
      Thursday: tourThursdayCheck,
      Friday: tourFridayCheck,
      Saturday: tourSaturdayCheck,
      Sunday: tourSundayCheck,
      SaturdayWorkingStatus: tourSaturdayWorkingCheck,
      ShipmentId: tourShipmentCheck,
      DeliveryId: tourDeliveryCheck,
      TourNo: tourNoCheck,
      TourName: tourNameCheck,
      TopServiceShopCode: selectedUpdateRegion?.Id
    };
    setTourUpdateLoading(true);
    axios
      .post('TourManagementUpdateInformation', data, {
        headers: { 'Content-Type': 'application/json' }
      })
      .then((response) => {
        if (response.data.ErrorCode === '0') {
          setresponseMessage({
            ErrorCode: 0,
            ErrorDescription: 'İşleminiz başarılıyla gerçekleşmiştir...'
          });
          setUyari(true);

          getTourInfo(selectedUpdateRegion?.Id);
          setTourInfoUpdatePopUp(false);
        } else {
          // Hata durumunda popup açık kalır; kullanıcı seçimlerini kaybetmeden tekrar deneyebilir
          setresponseMessage({
            ErrorCode: 1000,
            ErrorDescription: response.data.ErrorDescription
          });
          setUyari(true);
        }
        setTourUpdateLoading(false);
      })
      .catch((err) => {
        setresponseMessage({
          ErrorCode: 1000,
          ErrorDescription: 'Tur bilgileri güncellenemedi. Lütfen tekrar deneyiniz.'
        });
        setUyari(true);
        setTourUpdateLoading(false);
      });
  };

  const columns = [
    {
      field: 'Tur',
      headerName: 'Tur',
      width: 160,
      renderCell: (cellValues) => <Typography sx={{ fontWeight: 700, fontSize: 12 }}>{cellValues.value}</Typography>
    },
    { field: 'TUR_ADI', headerName: 'Tur Adı', width: 220 },
    ...DAY_FIELDS.map((day) => ({
      field: day.field,
      headerName: day.headerName,
      flex: 1,
      minWidth: 90,
      align: 'center',
      headerAlign: 'center',
      sortable: false,
      renderCell: (cellValues) => <DayMark active={!!cellValues.value} />
    })),
    {
      field: 'CUMARTESI_CALISMA_DURUMU',
      headerName: 'Cumartesi Çalışma',
      width: 150,
      align: 'center',
      headerAlign: 'center',
      sortable: false,
      renderCell: (cellValues) => <DayMark active={!!cellValues.value} />
    },
    {
      field: 'YUKLEME',
      headerName: 'Yükleme',
      width: 120,
      renderCell: (cellValues) => cellValues.value
        ? <Chip size="small" label={cellValues.value} sx={{ height: 22, fontSize: 11, borderRadius: '8px', backgroundColor: '#eceff1', color: DARK, fontWeight: 600 }} />
        : '-'
    },
    {
      field: 'TESLIMAT',
      headerName: 'Teslimat',
      width: 120,
      renderCell: (cellValues) => cellValues.value
        ? <Chip size="small" label={cellValues.value} sx={{ height: 22, fontSize: 11, borderRadius: '8px', backgroundColor: '#fff3e0', color: '#e65100', fontWeight: 600 }} />
        : '-'
    },
    {
      field: 'İşlemler',
      headerName: '',
      width: 100,
      align: 'center',
      sortable: false,
      filterable: false,
      renderCell: (cellValues) => (
        <Stack direction="row" spacing={1}>
          <Tooltip title="Güncelle">
            <IconButton
              size="small"
              sx={{ color: ORANGE, backgroundColor: DARK, borderRadius: '8px', '&:hover': { backgroundColor: '#212121' } }}
              onClick={() => {
                updateTourInformationClick(cellValues.row);
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Sil">
            <IconButton
              size="small"
              sx={{ color: '#c62828', border: '1px solid #ef9a9a', borderRadius: '8px', '&:hover': { backgroundColor: '#ffebee' } }}
              onClick={() => {
                deleteTour(cellValues.row);
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      )
    }
  ];

  const uyariKapat = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setUyari(false);
  };

  const dayStates = [
    { label: 'Pazartesi', value: tourMondayCheck, set: setTourMondayCheck },
    { label: 'Salı', value: tourTuesdayCheck, set: setTourTuesdayCheck },
    { label: 'Çarşamba', value: tourWednesdayCheck, set: setTourWednesdayCheck },
    { label: 'Perşembe', value: tourThursdayCheck, set: setTourThursdayCheck },
    { label: 'Cuma', value: tourFridayCheck, set: setTourFridayCheck },
    { label: 'Cumartesi', value: tourSaturdayCheck, set: setTourSaturdayCheck },
    { label: 'Pazar', value: tourSundayCheck, set: setTourSundayCheck }
  ];

  const summaryItems = [
    { label: 'Bölge', value: selectedUpdateRegion ? selectedUpdateRegion.Dsc : SessionUser.TopServiceShopCode },
    { label: 'Toplam Tur', value: tourGeneralInformations.length },
    { label: 'Cumartesi Çalışan', value: tourGeneralInformations.filter((i) => i.CUMARTESI_CALISMA_DURUMU).length },
    { label: 'Pazar Çalışan', value: tourGeneralInformations.filter((i) => i.PAZAR).length }
  ];

  return (
    <>
      <Backdrop className={classes.backdrop} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <Stack spacing={8}>
        <Snackbar
          sx={{ width: '100%' }}
          open={uyari}
          autoHideDuration={6000}
          onClose={uyariKapat}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center'
          }}
        >
          {responseMessage.ErrorCode === 1000 ? (
            <Alert
              icon={<MoodBadIcon />}
              style={{ minWidth: 500 }}
              sx={{
                '& .MuiAlert-icon': {
                  color: 'white'
                },
                height: 75,
                alignItems: 'center',
                verticalAlign: 'center'
              }}
              onClose={uyariKapat}
              variant="filled"
              severity="error"
            >
              {responseMessage.ErrorDescription}
            </Alert>
          ) : (
            <Alert
              icon={<TagFacesIcon />}
              style={{ minWidth: 500 }}
              sx={{
                '& .MuiAlert-icon': {
                  color: 'white'
                },
                height: 75,
                alignItems: 'center',
                verticalAlign: 'center'
              }}
              onClose={uyariKapat}
              variant="filled"
              severity="success"
            >
              {responseMessage.ErrorDescription}
            </Alert>
          )}
        </Snackbar>
      </Stack>
      <Box className={classes.root} component={Page}>
        <Card className={classes.card}>
          <CardHeader
            className={classes.cardHeader}
            avatar={<RouteIcon sx={{ color: ORANGE }} />}
            title="Tur Yönetimi"
            subheader="Bölgenin turlarını görüntüleyin, yeni tur oluşturun ve tur günlerini düzenleyin"
          />
          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
            {/* Sayfanın datagride sürekli render atmasından dolayı textfieldlarda yazı yazarken kasma oluyordu. Bu yüzden useRef tercih edildi. */}
            <Grid container spacing={3}>
              <Grid item xs={12} md={5}>
                <Typography className={classes.sectionLabel}>Bölge Seçimi</Typography>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} sm={8}>
                    <Autocomplete
                      id="combo-box-demo"
                      options={regionList}
                      getOptionLabel={(option) => option.Dsc}
                      value={selectedUpdateRegion}
                      size="small"
                      fullWidth
                      ListboxProps={listboxProps}
                      onChange={(event, val) => {
                        setSelectedUpdateRegion(val);
                      }}
                      renderInput={(params) => (
                        <TextField {...params} label="Bölge" variant="outlined" sx={inputSx} />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Button
                      fullWidth
                      startIcon={<SearchIcon />}
                      sx={darkButtonSx}
                      onClick={() => {
                        handleGetSelectedServiceTourInfo();
                      }}
                    >
                      Getir
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} md={7}>
                <Typography className={classes.sectionLabel}>Yeni Tur</Typography>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      variant="outlined"
                      size="small"
                      fullWidth
                      label="Tur Numarası"
                      inputRef={tourNoInputRef}
                      sx={inputSx}
                    />
                  </Grid>
                  <Grid item xs={12} sm={5}>
                    <TextField
                      variant="outlined"
                      size="small"
                      fullWidth
                      label="Tur Adı"
                      inputRef={tourNameInputRef}
                      sx={inputSx}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <Button
                      fullWidth
                      startIcon={<AddIcon />}
                      sx={darkButtonSx}
                      onClick={() => {
                        createTour();
                      }}
                    >
                      Tur Yarat
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>

            <Grid container spacing={1.5} sx={{ mt: 1 }}>
              {summaryItems.map((item) => (
                <Grid item xs={6} md={3} key={item.label}>
                  <Box className={classes.infoBox}>
                    <Typography className={classes.infoLabel}>{item.label}</Typography>
                    <Typography className={classes.infoValue} noWrap>{item.value}</Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>

        <Card className={classes.card}>
          <CardHeader
            className={classes.cardHeader}
            avatar={<RouteIcon sx={{ color: ORANGE }} />}
            title="Turlar"
          />
          <DataGrid
            className={classes.grid}
            style={{ height: 600, width: '100%' }}
            columns={columns}
            rows={tourGeneralInformations}
            getRowId={(rows) => rows.ID}
            rowsPerPageOptions={[25, 50, 100]}
            rowHeight={42}
            headerHeight={42}
            disableSelectionOnClick
          />
        </Card>
      </Box>

      {/* Tur Bilgileri Güncelleme PopUp */}
      <Dialog
        open={tourInfoUpdatePopUp}
        onClose={closeTourInfoPopUp}
        aria-describedby="alert-dialog-slide-description"
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: '16px' } }}
      >
        <DialogTitle className={classes.cardHeader} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: '12px', fontWeight: 700, color: DARK, letterSpacing: '0.5px', textTransform: 'uppercase' }}>
            <RouteIcon sx={{ color: ORANGE }} /> Tur Bilgilerini Güncelle
          </Box>
          <IconButton size="small" onClick={closeTourInfoPopUp} disabled={tourUpdateLoading}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: '16px !important', position: 'relative', pointerEvents: tourUpdateLoading ? 'none' : 'auto', opacity: tourUpdateLoading ? 0.6 : 1, transition: 'opacity 0.2s' }}>
          <Grid container spacing={1.5}>
            <Grid item xs={4}>
              <Box className={classes.infoBox}>
                <Typography className={classes.infoLabel}>Tur No</Typography>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#263238' }}>{tourNoCheck}</Typography>
              </Box>
            </Grid>
            <Grid item xs={8}>
              <Box className={classes.infoBox}>
                <Typography className={classes.infoLabel}>Tur Adı</Typography>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#263238' }} noWrap>{tourNameCheck}</Typography>
              </Box>
            </Grid>
          </Grid>

          {/* Tur günleri: 7 eşit kutu */}
          <Box className={classes.dialogSection}>
            <Typography className={classes.sectionLabel}>Tur Günleri</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(4, 1fr)', sm: 'repeat(7, 1fr)' }, gap: 1 }}>
              {dayStates.map((day) => (
                <Box
                  key={day.label}
                  role="button"
                  onClick={() => day.set(!day.value)}
                  className={`${classes.dayTile} ${day.value ? classes.dayTileActive : ''}`}
                >
                  <Typography sx={{ fontSize: '11px', fontWeight: 700, color: day.value ? ORANGE : '#78909c' }}>
                    {DAY_FIELDS.find((d) => d.headerName === day.label)?.short || day.label}
                  </Typography>
                  {day.value
                    ? <CheckIcon sx={{ fontSize: 18, color: ORANGE }} />
                    : <RemoveIcon sx={{ fontSize: 18, color: '#b0bec5' }} />}
                </Box>
              ))}
            </Box>
          </Box>

          {/* Operasyon ayarları: etiket solda, seçim sağda */}
          <Box className={classes.dialogSection}>
            <Typography className={classes.sectionLabel}>Operasyon</Typography>
            {[
              {
                label: 'Cumartesi Çalışma',
                value: !!tourSaturdayWorkingCheck,
                set: setTourSaturdayWorkingCheck,
                options: [{ value: true, label: 'Çalışıyor' }, { value: false, label: 'Çalışmıyor' }]
              },
              { label: 'Yükleme', value: tourShipmentCheck, set: setTourShipmentCheck, options: DAY_OPTIONS },
              { label: 'Teslimat', value: tourDeliveryCheck, set: setTourDeliveryCheck, options: DAY_OPTIONS }
            ].map((item, index) => (
              <Box key={item.label} className={classes.settingRow} sx={{ borderTop: index > 0 ? '1px solid #eceff1' : 'none' }}>
                <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#263238' }}>{item.label}</Typography>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  className={classes.toggleGroup}
                  value={item.value}
                  onChange={(event, val) => { if (val !== null) item.set(val); }}
                >
                  {item.options.map((o) => <ToggleButton key={String(o.value)} value={o.value}>{o.label}</ToggleButton>)}
                </ToggleButtonGroup>
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button sx={lightButtonSx} onClick={closeTourInfoPopUp} disabled={tourUpdateLoading}>
            Vazgeç
          </Button>
          <Button
            sx={{ ...darkButtonSx, px: 3, '&.Mui-disabled': { backgroundColor: DARK, color: ORANGE, opacity: 0.85 } }}
            startIcon={tourUpdateLoading ? <CircularProgress size={16} sx={{ color: ORANGE }} /> : <EditIcon />}
            disabled={tourUpdateLoading}
            onClick={() => {
              updateInformationsOfTour();
            }}
          >
            {tourUpdateLoading ? 'Güncelleniyor...' : 'Güncelle'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default TourManagement;
